import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Pencil, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

export function ShopCategoriesPanel({ onChanged }) {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/shop/categories");
      setCats(data);
    } catch { toast.error("Errore caricamento categorie"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/shop/categories", { name: newName.trim() });
      setNewName("");
      toast.success("Categoria creata");
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore creazione"); }
    finally { setBusy(false); }
  };

  const saveRename = async (c) => {
    setBusy(true);
    try {
      await api.put(`/admin/shop/categories/${c.id}`, { name: editName.trim() });
      toast.success("Categoria rinominata (aggiornati anche i prodotti)");
      setEditing(null);
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore rinomina"); }
    finally { setBusy(false); }
  };

  const toggle = async (c) => {
    try {
      await api.put(`/admin/shop/categories/${c.id}`, { active: !c.active });
      load(); onChanged?.();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cats.length) return;
    const arr = cats.slice();
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setCats(arr);
    try { await api.post("/admin/shop/categories/reorder", { ids: arr.map((c) => c.id) }); onChanged?.(); }
    catch { toast.error("Errore riordino"); load(); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/shop/categories/${c.id}`);
      toast.success("Categoria eliminata");
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore eliminazione"); }
  };

  return (
    <div className="bg-white border border-hair p-5 md:p-6" data-testid="shop-categories-panel">
      <div className="eyebrow mb-4">Categorie Shop</div>
      <div className="flex gap-2">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nuova categoria…"
          className="rounded-none" data-testid="category-new-name"
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), create())} />
        <button type="button" onClick={create} disabled={busy || !newName.trim()}
          className="min-h-[44px] shrink-0 inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase bg-ink text-bone px-4 hover:bg-gold transition-colors disabled:opacity-60"
          data-testid="category-create-btn"><Plus size={14} /> Crea</button>
      </div>
      <div className="mt-4 space-y-2">
        {cats.map((c, i) => (
          <div key={c.id} className="flex items-center gap-2 border border-hair p-2.5" data-testid={`category-row-${c.id}`}>
            <div className="flex flex-col">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 hover:text-gold disabled:opacity-30" data-testid={`category-up-${c.id}`}><ArrowUp size={13} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === cats.length - 1} className="p-1 hover:text-gold disabled:opacity-30" data-testid={`category-down-${c.id}`}><ArrowDown size={13} /></button>
            </div>
            {editing === c.id ? (
              <>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-none h-9 flex-1" data-testid={`category-rename-${c.id}`} />
                <button type="button" onClick={() => saveRename(c)} className="p-2 text-emerald-700 hover:bg-emerald-50" data-testid={`category-save-${c.id}`}><Check size={15} /></button>
                <button type="button" onClick={() => setEditing(null)} className="p-2 text-taupe hover:bg-bone"><X size={15} /></button>
              </>
            ) : (
              <>
                <span className={`flex-1 text-sm ${c.active ? "" : "text-taupe line-through"}`}>{c.name}</span>
                <label className="flex items-center gap-2 text-[11px] text-taupe cursor-pointer min-h-[32px]">
                  <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`category-active-${c.id}`} />
                  {c.active ? "Visibile" : "Nascosta"}
                </label>
                <button type="button" onClick={() => { setEditing(c.id); setEditName(c.name); }} className="p-2 hover:text-gold" data-testid={`category-edit-${c.id}`}><Pencil size={14} /></button>
                <button type="button" onClick={() => remove(c)} className="p-2 hover:text-red-700" data-testid={`category-delete-${c.id}`}><Trash2 size={14} /></button>
              </>
            )}
          </div>
        ))}
        {cats.length === 0 && <p className="text-xs text-taupe py-3 text-center">Nessuna categoria</p>}
      </div>
      <p className="text-[11px] text-taupe mt-3">Una categoria si elimina solo se vuota. La rinomina aggiorna automaticamente i prodotti assegnati.</p>
    </div>
  );
}
